import { useState, type SVGProps } from "react";
import { Camera as CameraGlyph } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CAMERA_ICONS, type CameraIcon } from "@/types/camera-plans";

type IconProps = SVGProps<SVGSVGElement>;

function BaseIcon({
  children,
  className,
  viewBox = "0 0 24 24",
  ...props
}: IconProps & { viewBox?: string }) {
  return (
    <svg
      viewBox={viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

function TripodIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <rect x="7" y="4" width="10" height="7" rx="2" />
      <path d="M12 11v4" />
      <path d="M9 20l3-5 3 5" />
      <path d="M6 20l3-5" />
      <path d="M18 20l-3-5" />
      <path d="M9 7h6" />
    </BaseIcon>
  );
}

function HandheldIcon(props: IconProps) {
  return <CameraGlyph {...props} />;
}

function DroneIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <circle cx="7" cy="7" r="2.5" />
      <circle cx="17" cy="7" r="2.5" />
      <circle cx="7" cy="17" r="2.5" />
      <circle cx="17" cy="17" r="2.5" />
      <rect x="9.5" y="9.5" width="5" height="5" rx="1.2" />
      <path d="M9.5 9.5L8 8" />
      <path d="M14.5 9.5L16 8" />
      <path d="M9.5 14.5L8 16" />
      <path d="M14.5 14.5L16 16" />
    </BaseIcon>
  );
}

function EBikeIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <circle cx="7" cy="17" r="3" />
      <circle cx="18" cy="17" r="3" />
      <path d="M7 17l4-6h3l4 6" />
      <path d="M11 11l-2-3h4" />
      <path d="M14 8h3" />
      <path d="M15 8l2 3" />
      <path d="M12.5 11h3" />
    </BaseIcon>
  );
}

function MotoIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      {/* wheels */}
      <circle cx="5.5" cy="17" r="3" />
      <circle cx="18.5" cy="17" r="3" />
      {/* frame */}
      <path d="M8.5 17l1-4.5h5l2-3.5" />
      {/* seat */}
      <path d="M7 12l2.5.5" />
      {/* fuel tank */}
      <path d="M9.5 12.5c2-.5 4-1.5 5.5-2.5" />
      {/* tall handlebars */}
      <path d="M16.5 9l.5-4" />
      <path d="M15 5h4" />
      {/* front fork */}
      <path d="M16.5 9l2 8" />
      {/* large engine */}
      <rect x="9" y="13.5" width="5" height="3" rx="0.7" />
      {/* exhaust */}
      <path d="M14 15.5l2.5 1" />
    </BaseIcon>
  );
}

function HelicopterIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M4 8.5h16" />
      <path d="M12 5v3.5" />
      <path d="M7 11.5h7.5a3.5 3.5 0 0 1 0 7H9a3 3 0 0 1-3-3v-1l-2-1" />
      <path d="M15 12l4-2v2l-4 1" />
      <path d="M14.5 14h3.5" />
      <path d="M8.5 18.5l-1.5 2" />
      <path d="M13.5 18.5l1.5 2" />
      <path d="M6 20.5h10" />
    </BaseIcon>
  );
}

function PTZIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M6 10c1.5-3 4-4.5 6-4.5s4.5 1.5 6 4.5c-1.5 3-4 4.5-6 4.5S7.5 13 6 10Z" />
      <circle cx="12" cy="10" r="2" />
      <path d="M9 18h6" />
      <path d="M12 14v4" />
      <path d="M8 21h8" />
    </BaseIcon>
  );
}

const supportedIconMap = {
  tripod: TripodIcon,
  handheld: HandheldIcon,
  drone: DroneIcon,
  ebike: EBikeIcon,
  moto: MotoIcon,
  helicopter: HelicopterIcon,
  ptz: PTZIcon,
} as const;

const legacyIconMap: Partial<
  Record<CameraIcon, keyof typeof supportedIconMap>
> = {
  shoulder: "handheld",
  steadicam: "handheld",
  crane: "tripod",
  robotic: "ptz",
};

export function getCameraIcon(icon: CameraIcon) {
  const resolvedIcon =
    icon in supportedIconMap
      ? (icon as keyof typeof supportedIconMap)
      : (legacyIconMap[icon] ?? "tripod");

  return supportedIconMap[resolvedIcon];
}

interface CameraIconPickerProps {
  value: CameraIcon;
  onChange: (icon: CameraIcon) => void;
  className?: string;
}

export function CameraIconPicker({
  value,
  onChange,
  className,
}: CameraIconPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = CAMERA_ICONS.filter((icon) =>
    icon.label.toLowerCase().includes(search.toLowerCase()),
  );

  const CurrentIcon = getCameraIcon(value);
  const currentLabel =
    CAMERA_ICONS.find((i) => i.value === value) ??
    CAMERA_ICONS.find((i) => i.value === (legacyIconMap[value] ?? "tripod"));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("gap-2 h-8", className)}
          title={currentLabel?.label ?? value}
        >
          <CurrentIcon className="h-4 w-4" />
          <span className="sr-only">{currentLabel?.label ?? value}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <Input
          placeholder="Search icons..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2 h-8 text-sm"
          autoFocus
        />
        <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
          {filtered.map((icon) => {
            const Icon = getCameraIcon(icon.value);
            return (
              <Button
                key={icon.value}
                variant={value === icon.value ? "secondary" : "ghost"}
                size="sm"
                className="justify-start gap-2 h-8 text-xs"
                onClick={() => {
                  onChange(icon.value);
                  setOpen(false);
                  setSearch("");
                }}
              >
                <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                {icon.label}
              </Button>
            );
          })}
          {filtered.length === 0 && (
            <p className="col-span-2 text-xs text-muted-foreground text-center py-2">
              No icons found
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
